/**
 * SWARM SILHOUETTES — arbitrary shapes the Mother can wear.
 * Spec: docs/BOSS-SWARM-MOTHER.md, "How arbitrary silhouettes are built".
 *
 * Maths gives the abstract formations directly — a ring is trigonometry. It
 * does not give an eagle. These are the shapes with no closed form, and they
 * are what make the boss memorable rather than merely dense.
 *
 * HOW A SHAPE IS DEFINED. Each silhouette is a coverage predicate over
 * normalised space: `(u, v) => boolean`, with u to the right and v up, both in
 * roughly [-1, 1]. A grid is sampled once at module load, the filled cells are
 * collected, and slots are distributed across them by even stride.
 *
 * WHY PREDICATES AND NOT PIXEL ART. A 96x64 bitmap of an eagle is 6,144
 * hand-authored characters that nobody can edit safely. A union of analytic
 * parts — body, swept wings, fanned tail — is a dozen readable lines, tunes by
 * changing a number, and cannot go subtly wrong in a way that only shows up on
 * screen.
 *
 * ROW-MAJOR SAMPLING IS LOad-BEARING. Cells are collected top-to-bottom,
 * left-to-right, exactly like the WALL formation's grid. That means slot n
 * sits at a comparable position in both, so a corridor cut through the wall's
 * left flank leaves the eagle missing its left wing. Damage disfigures, which
 * is a far better progress readout than any bar. Sample in a different order
 * and cumulative damage degenerates into speckle.
 */

export interface Station {
  x: number
  y: number
}

/** Sampling resolution. Fine enough for a readable wing, cheap to build once. */
const GRID_W = 112
const GRID_H = 72

/** Half-extents the shape is scaled into, in body-local units. */
const SPAN_X = 15.5
const SPAN_Y = 7.3

type Coverage = (u: number, v: number) => boolean

/** Smooth half-thickness taper: 1 at the root, 0 at the tip. */
const taper = (t: number, power = 0.55) => Math.pow(Math.max(0, 1 - t), power)

/**
 * THE EAGLE. Wings spanning the whole arena, wearing the player's own national
 * bird. Desecration, not decoration — the Lattice has taken the symbol along
 * with everything else.
 */
const eagle: Coverage = (u, v) => {
  const au = Math.abs(u)
  // Body: a narrow spindle down the centre.
  if (au < 0.07 + 0.035 * (1 - Math.abs(v)) && v > -0.52 && v < 0.5) return true
  // Head and beak.
  const hd = Math.hypot(u, (v - 0.6) * 1.5)
  if (hd < 0.11) return true
  if (au < 0.035 && v > 0.66 && v < 0.78) return true
  // Wings: swept back from the shoulder, thinning to feathered tips.
  if (au > 0.06 && au < 0.99) {
    const t = (au - 0.06) / 0.93
    const centre = 0.2 - 0.42 * t * t
    const half = 0.3 * taper(t, 0.7)
    if (Math.abs(v - centre) < half) return true
    // Primary feathers: separated fingers at the outer third.
    if (t > 0.62) {
      const finger = Math.floor((t - 0.62) * 26) % 2 === 0
      if (finger && Math.abs(v - centre) < half + 0.11) return true
    }
  }
  // Fanned tail.
  if (v < -0.45 && v > -0.86) {
    const t = (-0.45 - v) / 0.41
    if (au < 0.06 + 0.2 * t) return true
  }
  return false
}

/**
 * THE FIGHTER. A giant copy of the player's own aircraft.
 *
 * The centrepiece of the library and the single most memorable beat available:
 * the Mother has been watching this jet for three minutes and builds one. The
 * encounter shows it MALFORMED the first time and correct the second, which
 * costs one interpolation parameter and reads as the thing learning.
 *
 * Authored to match the Wraithwing planform — delta wing, chined forebody,
 * canted twin tails. It could later be baked from the player model itself, so
 * that changing the jet changes the Mother's impression of it.
 */
const fighter: Coverage = (u, v) => {
  const au = Math.abs(u)
  // Fuselage: chined, widest at mid-body, tapering to a pointed nose.
  const noseT = Math.max(0, (v - 0.2) / 0.75)
  if (au < 0.1 * (1 - noseT * noseT) + 0.02 && v > -0.8 && v < 0.95) return true
  // Delta wing: leading edge sweeps back from the shoulder.
  if (v < 0.28 && v > -0.55) {
    const t = (0.28 - v) / 0.83
    if (au < 0.06 + 0.92 * t * t) return true
  }
  // Canted twin tails.
  if (v < -0.4 && v > -0.85) {
    const t = (-0.4 - v) / 0.45
    const c = 0.16 + 0.2 * t
    if (Math.abs(au - c) < 0.05) return true
  }
  // Engine nacelles.
  if (v < -0.1 && v > -0.72 && Math.abs(au - 0.13) < 0.055) return true
  return false
}

/**
 * THE HAND. An open claw that closes toward the player.
 *
 * The only formation in the library that reads as PURSUIT — every other shape
 * sits and dares you to come to it. The encounter drives the closing by
 * interpolating toward a fist.
 */
const hand: Coverage = (u, v) => {
  const au = Math.abs(u)
  // Palm.
  if (Math.hypot(u / 0.42, (v + 0.28) / 0.36) < 1) return true
  // Four fingers, splayed and of uneven length — even fingers read as a rake.
  const FINGERS: Array<[number, number, number]> = [
    [-0.34, 0.62, 0.075],
    [-0.11, 0.82, 0.08],
    [0.13, 0.76, 0.08],
    [0.35, 0.58, 0.07],
  ]
  for (const [cx, len, w] of FINGERS) {
    const spread = cx * 1.5
    if (v > 0.02 && v < len) {
      const t = (v - 0.02) / (len - 0.02)
      const x = cx + spread * t * 0.35
      if (Math.abs(u - x) < w * (1 - t * 0.35)) return true
    }
  }
  // Thumb, angled off the left of the palm.
  if (u < -0.2 && u > -0.78 && v > -0.55 && v < -0.05) {
    const t = (-0.2 - u) / 0.58
    if (Math.abs(v - (-0.16 - 0.3 * t)) < 0.09 * (1 - t * 0.4)) return true
  }
  if (au > 1) return false
  return false
}

export type SilhouetteName = 'eagle' | 'fighter' | 'hand'

const COVERAGE: Record<SilhouetteName, Coverage> = { eagle, fighter, hand }

/**
 * Sample a coverage predicate into an evenly-distributed point set.
 *
 * Cells are collected row-major so slot order matches the WALL grid; see the
 * header note on why that ordering is load-bearing.
 */
function bake(fn: Coverage): Station[] {
  const cells: Station[] = []
  for (let gy = 0; gy < GRID_H; gy += 1) {
    // v runs +1 (top) to -1 (bottom) so row 0 is the top of the shape.
    const v = 1 - (gy / (GRID_H - 1)) * 2
    for (let gx = 0; gx < GRID_W; gx += 1) {
      const u = (gx / (GRID_W - 1)) * 2 - 1
      if (!fn(u, v)) continue
      cells.push({ x: u * SPAN_X, y: v * SPAN_Y })
    }
  }
  return cells
}

const BAKED: Record<SilhouetteName, Station[]> = {
  eagle: bake(eagle),
  fighter: bake(fighter),
  hand: bake(hand),
}

/**
 * Station for one slot in a silhouette.
 *
 * Slots are spread across the filled cells by even stride, so density is
 * uniform over the shape rather than clumping wherever the grid happened to
 * resolve more cells. A silhouette that bakes to fewer cells than there are
 * slots simply doubles up — the shape stays right, it just gets denser.
 */
export function silhouetteStation(
  name: SilhouetteName,
  slot: number,
  total: number,
): Station {
  const cells = BAKED[name]
  if (cells.length === 0) return { x: 0, y: 0 }
  const idx = Math.min(cells.length - 1, Math.floor((slot / total) * cells.length))
  return cells[idx]
}

/** Diagnostic: how many cells each silhouette resolved to. */
export function silhouetteCoverage(): Record<SilhouetteName, number> {
  return {
    eagle: BAKED.eagle.length,
    fighter: BAKED.fighter.length,
    hand: BAKED.hand.length,
  }
}

export { COVERAGE }
